import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IconButton, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useRef, useState } from "react";

const OuterContainer = styled("div")(({ theme }) => ({
  position: "relative",
  marginLeft: "-24px",
  marginRight: "-24px",
  [theme.breakpoints.up("sm")]: {
    marginLeft: "-40px",
    marginRight: "-40px",
  },
  "&:hover .scroll-arrow": {
    opacity: 1,
  },
}));

const ScrollContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isDragging",
})<{ $isDragging: boolean }>(({ theme, $isDragging }) => ({
  display: "flex",
  overflowX: "auto",
  gap: "16px",
  paddingTop: 0,
  paddingBottom: "8px",
  paddingLeft: "24px",
  paddingRight: "24px",
  scrollBehavior: $isDragging ? "auto" : "smooth",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  [theme.breakpoints.up("sm")]: {
    paddingLeft: "40px",
    paddingRight: "40px",
  },
}));

const ArrowButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "$position",
})<{ $position: "left" | "right" }>(({ theme, $position }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  left: $position === "left" ? "24px" : undefined,
  right: $position === "right" ? "24px" : undefined,
  zIndex: 10,
  backgroundColor: "white",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  opacity: 0,
  transition: "opacity 0.2s, background-color 0.2s",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  "&:disabled": {
    opacity: "0 !important",
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

interface HorizontalScrollContainerProps {
  children: React.ReactNode;
}

export default function HorizontalScrollContainer({
  children,
}: HorizontalScrollContainerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const updateScrollButtons = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    updateScrollButtons();
    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    const observer = new MutationObserver(updateScrollButtons);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
      observer.disconnect();
    };
  }, [updateScrollButtons]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollAmount = 300;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const container = scrollRef.current;
    if (!container) return;

    setIsDragging(true);
    setStartX(e.touches[0].pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const container = scrollRef.current;
    if (!container) return;

    const x = e.touches[0].pageX - container.offsetLeft;
    const walk = x - startX;
    container.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <OuterContainer>
      {!isMobile && (
        <>
          <ArrowButton
            className="scroll-arrow"
            $position="left"
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            size="small"
          >
            <ChevronLeftIcon />
          </ArrowButton>
          <ArrowButton
            className="scroll-arrow"
            $position="right"
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            size="small"
          >
            <ChevronRightIcon />
          </ArrowButton>
        </>
      )}
      <ScrollContainer
        ref={scrollRef}
        $isDragging={isDragging}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </ScrollContainer>
    </OuterContainer>
  );
}
